import * as React from 'react';
import { AppDispatch } from '@app/store';
import UserApi from '@app/api/user-api/user.actions';
import { textInputRequiredOnly, textInputValidate, isValidPhone } from '@app/lib/validator';
import { Saver } from '@app/components/Saver';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { PageSection, Title, TextInput, Form, FormGroup } from '@patternfly/react-core';
import Page from '@app/components/Page';
import '../admin/Admin.css';
import { connect } from 'react-redux';
import { State } from '@app/store/root-reducer';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import Loader from '@app/components/Loader';
import { Helmet } from 'react-helmet/es/Helmet';
import ResetPassword from '@app/pages/auth/ResetPassword';
import ApiKeys from '@app/pages/admin/ApiKeys';
import UserSession from '@app/common/user-session';

interface ProfileState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  isLoading: boolean;
  messageType?: string;
  responseMessage?: string;
}

class Profile extends React.Component<AppDispatch & { partnerId: string; userId: string; dispatch: any }, ProfileState> {
  constructor(props) {
    super(props);

    this.state = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
      isLoading: false,
    };
  }

  componentDidMount = () => {
    try {
      this.loadProfile();
    } catch (error) {
      this.setState(() => ({
        message: 'There was an error getting the user profile',
      }));
    }
  };

  loadProfile = async () => {
    const { userId } = this.props;
    const user = await UserApi.get(userId);

    this.setState(() => ({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    }));
  };

  handleChange = <K extends keyof ProfileState>(name: K, value: ProfileState[K]) => {
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  handleSubmit = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    try {
      const { userId } = this.props;
      const { firstName, lastName, phone } = this.state;
      const payload = {
        first_name: firstName,
        last_name: lastName,
        phone: phone,
      };

      const result = await UserApi.update(userId, payload);

      if (result?.data?.request_id) {
        this.setState(() => ({
          message: 'User was updated successfully!',
        }));
      } else {
        this.setState(() => ({
          message: 'There was an error updating the user',
        }));
      }
    } catch (error) {
      this.setState(() => ({
        message: 'There was an error updating the user',
      }));
    }
  };

  render() {
    const { partnerId } = this.props;
    const { firstName, lastName, email, phone, isLoading, responseMessage } = this.state;
    const phoneIsValid = isValidPhone(phone);
    const phoneErrorState = textInputValidate(phone, true, phoneIsValid, 'Invalid phone number');
    const formValid = firstName && lastName && phone && phoneIsValid;
    const { theme } = this.context;

    return (
      <React.Fragment>
        <Helmet>
          <title>Profile</title>
        </Helmet>
        {isLoading ? (
          <Loader />
        ) : (
          <Page
            tag="div"
            className={`cr-page px-1 pt-0 acl-form position-relative ${theme}`}
            title=""
            breadcrumbs={[
              { name: 'User', active: false, url: '/profile' },
              { name: 'Profile', active: true }
            ]}
            actionButton={null}
          >
            <Row className="g-1">
              <Col md={6} className="d-flex">
                <Card className="h-100 w-100">
                  <CardHeader className="py-0 px-2 fw-bold d-flex align-items-center bg-transparent" style={{minHeight: '32px'}}>
                    <div className="w-100 d-flex justify-content-between">
                      <div className="icon-heading-container">
                        <div className="icon-heading" style={{fontSize: '0.9rem'}}>Profile</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="p-1 px-2 d-flex flex-column" style={{paddingTop: '0.25rem', paddingBottom: '0.25rem'}}>
                    <FormGroup
                      label="First Name"
                      isRequired
                      fieldId="firstName"
                      className="form-item mb-1"
                      helperTextInvalid=" Required field"
                      helperTextInvalidIcon={<ExclamationCircleIcon />}
                      validated={textInputRequiredOnly(firstName)}
                    >
                      <TextInput
                        isRequired
                        type="text"
                        id="firstName"
                        name="firstName"
                        validated={textInputRequiredOnly(firstName)}
                        value={firstName}
                        onChange={(e) => this.handleChange('firstName', e)}
                        className="form-control form-control-sm"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Last Name"
                      isRequired
                      fieldId="lastName"
                      className="form-item mb-1"
                      helperTextInvalid=" Required field"
                      helperTextInvalidIcon={<ExclamationCircleIcon />}
                      validated={textInputRequiredOnly(lastName)}
                    >
                      <TextInput
                        isRequired
                        type="text"
                        id="lastName"
                        name="lastName"
                        validated={textInputRequiredOnly(lastName)}
                        value={lastName}
                        onChange={(e) => this.handleChange('lastName', e)}
                        className="form-control form-control-sm"
                      />
                    </FormGroup>
                    <FormGroup label="Email" fieldId="email" className="form-item mb-1">
                      <TextInput
                        isDisabled
                        type="text"
                        id="email"
                        name="email"
                        value={email}
                        className="form-control form-control-sm"
                      />
                    </FormGroup>
                    <FormGroup
                      label="Phone Number"
                      isRequired
                      fieldId="phone"
                      className="form-item mb-1"
                      helperTextInvalid={phoneErrorState.errorText}
                      helperTextInvalidIcon={<ExclamationCircleIcon />}
                    >
                      <TextInput
                        isRequired
                        type="tel"
                        id="phone"
                        name="phone"
                        value={phone}
                        onChange={(e) => this.handleChange('phone', e)}
                        className="form-control form-control-sm"
                      />
                    </FormGroup>
                  </CardBody>
                </Card>
              </Col>
              <Col md={6} className="d-flex">
                <Card className="h-100 w-100">
                  <CardHeader className="py-0 px-2 fw-bold d-flex align-items-center bg-transparent" style={{minHeight: '32px'}}>
                    <div className="w-100 d-flex justify-content-between">
                      <div className="icon-heading-container">
                        <div className="icon-heading" style={{fontSize: '0.9rem'}}>Reset Password</div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody className="p-1 px-2 d-flex flex-column" style={{paddingTop: '0.25rem', paddingBottom: '0.25rem'}}>
                    <ResetPassword hideVideoBackground={true} compact={true} dispatch={this.props.dispatch} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row className="g-1 mt-1">
              <Col md={12}>
                <ApiKeys />
              </Col>
            </Row>
          </Page>
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  (state: State) => {
    // Check if user is impersonating a partner space
    const isImpersonating = UserSession.getParam('isImpersonating');
    
    // If impersonating, get partnerId from UserSession (localStorage)
    // Otherwise, get it from Redux store
    const partnerId = isImpersonating 
      ? UserSession.getPartnerId() 
      : state.common.auth.session?.partnerId;
    
    return {
      partnerId,
      userId: state.common.auth.session?.userId,
    };
  },
  (dispatch) => ({ dispatch })
)(Profile);
